import type { JSX } from "react";

import { PageState } from "api/generated/model";
import { PageKey } from "org/pageState/pageKey";

import { Codecs, PageType } from "../types";
import { unwrapPageStateAs } from "../utils";

import { PageStateManagerInner } from "./pageStateManagerInner";
import type { RenderContentInnerArgs } from "./pageStateManagerInner";

type PageStateVariant<PT extends PageType> = Extract<PageState, { type: PT }>;

interface RenderContentArgs<RawPageState> {
    pageState: RawPageState | undefined;
    onPageStateChange: ((pageState: RawPageState) => void) | undefined;
    onCreateShareableLink: (() => void) | undefined;
}

interface PageStateManagerProps<PT extends PageType, RawPageState> {
    pageKey: Extract<PageKey, { type: PT }>;
    renderContent: (args: RenderContentArgs<RawPageState>) => JSX.Element;
    renderError: (code: number | undefined) => JSX.Element;
    renderLoading: () => JSX.Element;
    codecs: Codecs<RawPageState, PageStateVariant<PT>>;
    preservedSearchParams?: Array<string>;

    // An optional hook to modify pageState in memory once after it's initialized from localStorage or default values.
    // Practically it's used for those pages, where filter can be defined through URL, but we don't want the filter from URL
    // overrides values in localStorage on init.
    onInitInMemoryPageStateUpdate?: (pageState: RawPageState) => RawPageState;
}

// PageStateManager is responsible for providing and storing pageState (filters and visible columns)
// for a particular page (identified by PageKey).
//
// It encapsulates functionality that related to pageState and shareable links, and enables almost
// seamless integration with new pages.
//
// The algorithm to obtain filters and columns is the following:
// * If shareableLinkId is present in URL params, fetch the pageState from the API
// * If there is a value for given pageKey in sessionStorage/localStorage, then use it
// * Otherwise use predefined default values for the given pageKey.
function PageStateManager<PT extends PageType, RawPageState>(
    props: PageStateManagerProps<PT, RawPageState>
): JSX.Element {
    const {
        pageKey,
        renderContent,
        renderLoading,
        renderError,
        preservedSearchParams,
        codecs,
        onInitInMemoryPageStateUpdate,
    } = props;
    const pageType: PT = pageKey.type;

    const renderContentInner = (args: RenderContentInnerArgs): JSX.Element => {
        const { pageState, onPageStateChange, onCreateShareableLink } = args;
        const pageStateTyped: PageStateVariant<PT> = unwrapPageStateAs(
            pageState,
            pageType
        );
        const rawPageState: RawPageState = codecs.decode(pageStateTyped);
        const rawOnPageStateChange = (raw: RawPageState): void => {
            onPageStateChange(codecs.encode(raw));
        };

        return renderContent({
            pageState: rawPageState,
            onPageStateChange: rawOnPageStateChange,
            onCreateShareableLink,
        });
    };

    const onInitInMemoryPageStateUpdateInner = (
        pageState: PageState
    ): PageState => {
        // If the onInitInMemoryPageStateUpdate hook is not present, then it behaves just like the identity function.
        if (onInitInMemoryPageStateUpdate === undefined) {
            return pageState;
        }

        const pageStateTyped: PageStateVariant<PT> = unwrapPageStateAs(
            pageState,
            pageType
        );
        const rawPageState: RawPageState = codecs.decode(pageStateTyped);
        const newRawPageState: RawPageState =
            onInitInMemoryPageStateUpdate(rawPageState);
        return codecs.encode(newRawPageState);
    };

    return (
        <PageStateManagerInner
            pageKey={pageKey}
            renderError={renderError}
            renderLoading={renderLoading}
            preservedSearchParams={preservedSearchParams}
            renderContent={renderContentInner}
            onInitInMemoryPageStateUpdate={onInitInMemoryPageStateUpdateInner}
        />
    );
}

function ConditionalPageStateManager<PT extends PageType, RawPageState>(
    props: PageStateManagerProps<PT, RawPageState> & { isEnabled: boolean }
): JSX.Element {
    const { isEnabled, ...remainingProps } = props;
    return props.isEnabled ? (
        <PageStateManager {...remainingProps} />
    ) : (
        props.renderContent({
            pageState: undefined,
            onPageStateChange: undefined,
            onCreateShareableLink: undefined,
        })
    );
}

export {
    PageStateManager,
    ConditionalPageStateManager,
    type RenderContentArgs,
};
